import React from 'react';

import { Column, Row } from '@xingternal/xing-grid';

import { ClientOnly } from '@/components/_common';
import { ActionsContainer } from '@/components/posting-header-actions';
import MeatballMenu from '@/components/posting-header-actions/components/meatball-menu';
import { ApplyConfirmation } from '@/components/posting-header/components/apply-confirmation';
import { OneClickApplyButtonsProvider } from '@/contexts';
import { useBreakpoints, useLoginStates, useOneClickApplyExperiment } from '@/hooks';

import { HeaderProps } from '../header.types';
import { useAppliedConfirmation, useStickyHeader } from '../hooks';
import { Divider } from './divider/divider';
import { StickyHeader } from './sticky-header/sticky-header';
import { TitleContainer } from './title-container';
import { PublishedDate } from './title-container/published-date';

import * as S from './header.styles';

export const Header: React.FC<HeaderProps> = ({ job }) => {
  const { ref, showStickyHeader, stickyHeaderWidth } = useStickyHeader();
  const appliedDate = useAppliedConfirmation(job);
  const { isMobile } = useBreakpoints();
  const { isLoggedIn } = useLoginStates();
  const { isExperimentActive: isOneClickApplyExperimentActive } = useOneClickApplyExperiment();

  // Avoid showing the divider if we are on a small screen or if a summary is
  // available. We don't need to check the experiment here because we are
  // already checking it in the GraphQL query that gives us the job. Therefore,
  // we can assume that if the summary is there we will display it.
  const showSummary = !!job.summary && (!job.language || ['en', 'de'].includes(job.language));
  const showDivider = !isMobile && !showSummary;

  const userDidNotApply = !appliedDate;
  const isXingApplication = job.application?.__typename === 'JobXingApplication';
  const isOneClickExperiment = isLoggedIn && isOneClickApplyExperimentActive && isXingApplication && userDidNotApply;
  const showMeatballMenu = (isLoggedIn && !isMobile) || isOneClickExperiment;

  return (
    <div data-testid="job-details-header" data-cy="job-details-header" ref={ref}>
      <OneClickApplyButtonsProvider>
        <div data-testid="static-job-details-header" data-cy="static-job-details-header">
          <S.StyledNavigation job={job} />
          {appliedDate ? (
            <ClientOnly>
              <ApplyConfirmation appliedDate={appliedDate} />
            </ClientOnly>
          ) : null}
          <Row>
            <Column size={12}>
              <S.PublishedDateRow>
                <PublishedDate />
                {showMeatballMenu ? <MeatballMenu isOneClickExperiment={isOneClickExperiment} /> : null}
              </S.PublishedDateRow>
            </Column>
          </Row>
          <Row>
            <S.TitleWrapper size={12}>
              <TitleContainer />
            </S.TitleWrapper>
          </Row>
          <Row>
            <Column size={12}>
              <ActionsContainer />
            </Column>
          </Row>
        </div>
        {showDivider ? <Divider /> : null}
        {showStickyHeader && <StickyHeader width={stickyHeaderWidth} job={job} />}
      </OneClickApplyButtonsProvider>
    </div>
  );
};
